import { IndividualDesignationListedTypes } from '@interfaces/models/individual-designation-types';
import { InternalTransferState } from '@interfaces/state/internal-transfer/internal-transfer.interface';
import { createSelector } from '@ngrx/store';
import {
  AccountTypes,
  Eligible_Joint_Account_Types_Pattern,
  FlowTypes,
  GiftThresholds,
  Move_To_New,
  TransactionTypesEnum,
} from '@shared/models';
import { parseStrDefaultToZero } from '@sharedUtils';
import { getTotalGiftAmount, GiftingState } from '@store/reducers/gifting/gifting.reducer';

import * as fromFeature from '../../reducers';
import {
  getAccountTypeSelection,
  getClientStatusSelection,
  getFlowTypeSelection,
  getIRADesignationSelection,
  getIsClient,
  getIsDivorceFlow,
  getIsIndividualAccount,
  getIsIndividualSingleExecutor,
  getIsInheritanceFlow,
  getIsInheritedIraFlow,
  getIsInternalTransferFlow,
  getIsIRAAccount,
  getIsIRAExecutor,
  getIsIRASingleExecutor,
  getIsIRAToEstateFlow,
  getIsIRATrust,
  getIsJointAccount,
  getIsMinorFlow,
  getIsMultiInheritanceFlow,
  getIsNonVanguardGiftingFlow,
  getIsOnlyTrustee,
  getIsOther,
  getIsProspect,
  getIsScaleGifting,
  getIsSpouse,
  getIsTrustAccount,
  getIsTrusteeOrg,
  getIsTrustToTrust,
  getIsTrustToTrustFlow,
  getIsVanguardGiftingFlow,
  getSelectExperimentGroup,
  getSpouseStatusSelection,
  getTypeOfTrust,
  TriageState,
} from '../../reducers/triage/triage.reducer';
import { selectGiftingState } from '../gifting/gifting.selectors';
import {
  isMultiSignerForEstateAccount,
  selectInternalTransferState,
  selectInternalTransferTransactionType,
} from '../internal-transfer/internal-transfer.selectors';
import { selectTransactionType } from '../selected-client-accounts/selected-client-accounts.selectors';
import { selectIsNewAccountTransactionExist } from '../transaction-state/transaction-state.selectors';

export const selectTriageState = createSelector(fromFeature.getTriageState, (state) => state);

export const clientStatusSelection = createSelector(selectTriageState, getClientStatusSelection);
export const spouseStatusSelection = createSelector(selectTriageState, getSpouseStatusSelection);
export const flowTypeSelection = createSelector(selectTriageState, getFlowTypeSelection);
export const selectAccountType = createSelector(selectTriageState, getAccountTypeSelection);
export const selectExperimentGroup = createSelector(selectTriageState, getSelectExperimentGroup);
export const selectIRADesignation = createSelector(selectTriageState, getIRADesignationSelection);
export const selectIndividualDesignation = createSelector(
  selectTriageState,
  (state): IndividualDesignationListedTypes => state.individualDesignation,
);

export const selectIsIndividualDesignationExecutor = createSelector(
  selectTriageState,
  (state) => state.individualDesignation === IndividualDesignationListedTypes.EXECUTOR,
);

export const isEligibleClient = (state: TriageState) => {
  return getIsClient(state) && !getIsOther(state);
};

export const isEligibleProspect = (state: TriageState) => {
  return getIsProspect(state) && (getIsIRAAccount(state) || getIsIndividualAccount(state));
};

export const isGiftingFlowUsingToggle = (state: TriageState) =>
  getIsVanguardGiftingFlow(state) || getIsNonVanguardGiftingFlow(state);
export const allowedGiftingFlows = (state: TriageState) =>
  getIsIndividualAccount(state) || getIsScaleGifting(state);

export const selectIsClient = createSelector(selectTriageState, getIsClient);
export const selectIsProspect = createSelector(selectTriageState, getIsProspect);
export const selectIsSpouse = createSelector(selectTriageState, getIsSpouse);

export const selectIsEligibleClient = createSelector(selectTriageState, isEligibleClient);
export const selectIsEligibleProspect = createSelector(selectTriageState, isEligibleProspect);

export const selectIsIndvAccount = createSelector(selectTriageState, getIsIndividualAccount);
export const selectIsJointAccount = createSelector(selectTriageState, getIsJointAccount);
export const selectIsTrustAccount = createSelector(selectTriageState, getIsTrustAccount);
export const selectIsIraAccount = createSelector(selectTriageState, getIsIRAAccount);

export const selectIsGiftingFlowUsingToggle = createSelector(
  selectTriageState,
  isGiftingFlowUsingToggle,
);
export const selectAllowedGiftingFlows = createSelector(
  selectTriageState,
  (triageState: TriageState) =>
    isGiftingFlowUsingToggle(triageState) && allowedGiftingFlows(triageState),
);

export const selectIsMXMForExecutor = createSelector(
  selectInternalTransferState,
  isMultiSignerForEstateAccount,
);

//Possibly refactor this to add more flows?
export const selectInternalTransferCanOpenNewAccount = createSelector(
  selectInternalTransferState,
  (internalTransferState: InternalTransferState) => {
    const { transferFrom, transferTo } = internalTransferState.triage;
    return (
      (transferFrom === AccountTypes.INDV || transferFrom === AccountTypes.ESTATE) &&
      transferTo === AccountTypes.INDV
    );
  },
);

export const selectIsMultiSignerGiftingScenario = createSelector(
  selectTriageState,
  selectGiftingState,
  (triageState: TriageState, giftingState: GiftingState) => {
    const accountType = getAccountTypeSelection(triageState);
    const giftAmount = parseStrDefaultToZero(getTotalGiftAmount(giftingState));

    if (!!accountType && !!giftAmount && getIsJointAccount(triageState)) {
      return (
        (accountType === AccountTypes.JOINT_TENANTS_WROS && giftAmount > GiftThresholds['500K']) ||
        (Eligible_Joint_Account_Types_Pattern.test(accountType) &&
          accountType !== AccountTypes.JOINT_TENANTS_WROS)
      );
    }

    return false;
  },
);

export const selectIsIRAExecutor = createSelector(selectTriageState, getIsIRAExecutor);
export const selectIsIRASingleExecutor = createSelector(selectTriageState, getIsIRASingleExecutor);
export const selectIsIndividualSingleExecutor = createSelector(
  selectTriageState,
  getIsIndividualSingleExecutor,
);
export const selectIsTrusteeOrg = createSelector(selectTriageState, getIsTrusteeOrg);
export const selectIsSingleTrustee = createSelector(selectTriageState, getIsOnlyTrustee);
export const selectIsTrustee = createSelector(selectTriageState, getIsIRATrust);
export const selectisIRAToEstateFlow = createSelector(selectTriageState, getIsIRAToEstateFlow);
export const selectTypeOfTrust = createSelector(selectTriageState, getTypeOfTrust);
export const selectIsTrustToTrust = createSelector(selectTriageState, getIsTrustToTrust);
export const selectIsTrustToTrustFlow = createSelector(selectTriageState, getIsTrustToTrustFlow);
export const selectIsMultiInheritanceFlow = createSelector(
  selectTriageState,
  getIsMultiInheritanceFlow,
);
export const selectIsInheritanceFlow = createSelector(selectTriageState, (state) =>
  getIsInheritanceFlow(state),
);
export const selectIsDivorceFlow = createSelector(selectTriageState, getIsDivorceFlow);

export const selectIsManualAccountOpeningRequired = createSelector(
  flowTypeSelection,
  selectIsIndividualDesignationExecutor,
  selectIsIRASingleExecutor,
  selectIsTrustee,
  selectIsTrustToTrustFlow,
  (
    flowType: FlowTypes | null,
    isIndividualExecutor: boolean,
    isIRAExecutor: boolean,
    isIRATrustee: boolean,
    isTrustToTrust: boolean,
  ) =>
    flowType === FlowTypes.INHERITANCE &&
    (isIndividualExecutor || isIRAExecutor || isIRATrustee || isTrustToTrust),
);

export const selectIsOpeningNewAccountAutomatically = createSelector(
  flowTypeSelection,
  selectTransactionType,
  selectInternalTransferTransactionType,
  selectIsManualAccountOpeningRequired,
  selectIsNewAccountTransactionExist,
  (
    flowType: FlowTypes | null,
    transactionType: TransactionTypesEnum | null,
    internalTransferTransactionType: TransactionTypesEnum | null,
    isManualAccountOpeningRequired: boolean,
    hasNewAccountTransferInMultoInheritance: boolean,
  ): boolean =>
    (flowType === FlowTypes.INTERNAL_TRANSFER &&
      !!internalTransferTransactionType &&
      Move_To_New.has(internalTransferTransactionType)) ||
    (flowType === FlowTypes.MULTI_INHERITANCE && hasNewAccountTransferInMultoInheritance) ||
    (!!transactionType && Move_To_New.has(transactionType) && !isManualAccountOpeningRequired),
);

export const selectIsInheritedIraFlow = createSelector(selectTriageState, getIsInheritedIraFlow);

export const selectIsIraOrInheritedIraFlow = createSelector(
  selectIsIraAccount,
  selectIsInheritedIraFlow,
  (isIraAccount, isInheritedIraFlow) => isIraAccount || isInheritedIraFlow,
);

export const selectIsInternalTransferFlow = createSelector(
  selectTriageState,
  getIsInternalTransferFlow,
);

export const selectIsMinorFlow = createSelector(selectTriageState, getIsMinorFlow);
